import { memo, useRef, useState } from 'react'
import { Text } from 'common-components'
import ActionsFlyout from 'common-components/ActionsFlyout'
import { IFlyoutMenuItem } from 'common-components/ActionsFlyout/types'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import { useDropdownState } from 'hooks/useDropdownState'
import UploadIcon from 'assets/svgr-icons/Upload.svg'
import MicrophoneIcon from 'assets/svgr-icons/Microphone.svg'
import * as Styled from './styled'

interface IProps {
  onUploadClick: () => void
  onMyVoicesClick: () => void
  onFilesDropped: (files: FileList) => void
}

// The "Add voice file" dashed card: click opens the Upload / My Voices menu,
// and the card doubles as a drop target for a voice file.
const AddVoiceFile: React.FC<IProps> = props => {
  const { onUploadClick, onMyVoicesClick, onFilesDropped } = props
  const anchorRef = useRef<HTMLButtonElement>(null)
  const { isOpen, toggleDropdown, closeDropdown } = useDropdownState()
  const [isDragActive, setIsDragActive] = useState(false)
  // dragenter/dragleave also fire for the card's children — count the pairs so
  // only leaving the card itself deactivates the state.
  const dragCounter = useRef(0)

  const onDragEnter = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    dragCounter.current++
    setIsDragActive(true)
  }

  const onDragLeave = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragActive(false)
    }
  }

  const onDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const onDrop = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    dragCounter.current = 0
    setIsDragActive(false)
    if (event.dataTransfer.files.length) {
      onFilesDropped(event.dataTransfer.files)
    }
  }

  const actions: IFlyoutMenuItem[] = [
    {
      key: T.UPLOAD_LABEL,
      title: translate(T.UPLOAD_LABEL_SIDEBAR),
      icon: <UploadIcon />,
      onClick: onUploadClick,
      testId: 'add-voice-upload-action'
    },
    {
      key: T.MY_VOICES_LABEL,
      title: translate(T.MY_VOICES_LABEL),
      icon: <MicrophoneIcon />,
      iconSize: { width: 20, height: 20 },
      isStrokedIcon: true,
      onClick: onMyVoicesClick,
      testId: 'add-voice-my-voices-action'
    }
  ]

  return (
    <>
      <Styled.AddVoiceCard
        ref={anchorRef}
        type="button"
        $isDragActive={isDragActive}
        onClick={toggleDropdown}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        data-testid="add-voice-file-card"
      >
        {isDragActive ? (
          <>
            <Styled.UploadIcon />
            <Text type="h2" color="secondarySemiDark">
              {translate(T.UPLOAD_OR_DRAG_DROP_VOICE_LABEL)}
            </Text>
          </>
        ) : (
          <>
            <Styled.AddIcon />
            <Text type="h2" color="secondarySemiDark">
              {translate(T.ADD_VOICE_FILE_LABEL)}
            </Text>
          </>
        )}
      </Styled.AddVoiceCard>
      <ActionsFlyout
        isOpen={isOpen}
        anchorEl={anchorRef.current}
        actions={actions}
        onClose={closeDropdown}
        flyoutOptions={{
          position: 'bottom',
          alignmentX: 'left',
          extraOffset: 8,
          // center the ~180px menu under the 348px card
          offsetX: 84
        }}
        isMobileWithDrawer
      />
    </>
  )
}

export default memo(AddVoiceFile)
